/**
 * Minimal, dependency-free ZIP reader for the in-app guide importer.
 *
 * Parses the central directory and inflates entries with the browser-native
 * DecompressionStream('deflate-raw'). Supports stored (method 0) and deflated
 * (method 8) entries — which is everything a normal .zip of text + images uses.
 * No encryption / ZIP64 support (not needed for a help guide bundle).
 */

const EOCD_SIG = 0x06054b50;
const CDH_SIG = 0x02014b50;
const LFH_SIG = 0x04034b50;

function isDecompressionAvailable(): boolean {
  return typeof (globalThis as any).DecompressionStream === 'function';
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const DS = (globalThis as any).DecompressionStream;
  const ds = new DS('deflate-raw');
  const stream = new Blob([data]).stream().pipeThrough(ds);
  const ab = await new Response(stream).arrayBuffer();
  return new Uint8Array(ab);
}

function findEocdOffset(view: DataView): number {
  // EOCD is at the end, before an optional comment (<= 65535 bytes).
  const len = view.byteLength;
  const min = Math.max(0, len - (22 + 0xffff));
  for (let i = len - 22; i >= min; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) return i;
  }
  return -1;
}

export interface UnzipResult {
  /** Map of entry path (as stored in the zip) → file bytes. Directories omitted. */
  files: Map<string, Uint8Array>;
}

export async function unzip(buffer: ArrayBuffer): Promise<UnzipResult> {
  if (!isDecompressionAvailable()) {
    throw new Error('Your browser does not support in-page decompression (DecompressionStream). Use a recent Chrome/Edge/Firefox.');
  }

  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  const eocd = findEocdOffset(view);
  if (eocd < 0) throw new Error('Not a valid .zip file (no end-of-central-directory record found).');

  const entryCount = view.getUint16(eocd + 10, true);
  let cdOffset = view.getUint32(eocd + 16, true);

  const files = new Map<string, Uint8Array>();
  const decoder = new TextDecoder('utf-8');

  let p = cdOffset;
  for (let n = 0; n < entryCount; n++) {
    if (view.getUint32(p, true) !== CDH_SIG) {
      throw new Error('Corrupt zip: central directory header signature mismatch.');
    }
    const method = view.getUint16(p + 10, true);
    const compressedSize = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOffset = view.getUint32(p + 42, true);
    const name = decoder.decode(bytes.subarray(p + 46, p + 46 + nameLen));

    p += 46 + nameLen + extraLen + commentLen;

    if (name.endsWith('/')) continue; // directory entry

    // Resolve the actual data start from the local file header.
    if (view.getUint32(localOffset, true) !== LFH_SIG) {
      throw new Error(`Corrupt zip: local header signature mismatch for "${name}".`);
    }
    const lhNameLen = view.getUint16(localOffset + 26, true);
    const lhExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + lhNameLen + lhExtraLen;
    const compressed = bytes.subarray(dataStart, dataStart + compressedSize);

    let content: Uint8Array;
    if (method === 0) {
      content = compressed.slice();
    } else if (method === 8) {
      content = await inflateRaw(compressed);
    } else {
      throw new Error(`Unsupported compression method ${method} for "${name}".`);
    }
    files.set(name, content);
  }

  return { files };
}

/** Decode bytes as UTF-8 text. */
export function bytesToText(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes);
}

/** Last path segment of a zip entry path, lower-cased for matching. */
export function basename(pathStr: string): string {
  const clean = pathStr.replace(/\\/g, '/').replace(/\/+$/, '');
  const idx = clean.lastIndexOf('/');
  return idx >= 0 ? clean.slice(idx + 1) : clean;
}
